/**
 * CreditRepModal — Create / Edit Credit Representative modal
 *
 * Handles both:
 *  - Create mode: "Add Credit Representative"
 *  - Edit mode: Pre-populates all fields with existing CR data
 *
 * Schema facts (from compliance-cr.ts route):
 *  - entityName (required) — NOT firstName/lastName
 *  - appointmentType: "full" | "limited" | "provisional"
 *  - crStatus: "active" | "suspended" | "terminated" | "pending"
 *  - complianceStatus: "compliant" | "non_compliant" | "under_review" | "remediation_required"
 *  - entityType: optional string (individual/company distinction)
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CrData {
  id?: string;
  entityName?: string | null;
  appointmentType?: string | null;
  entityType?: string | null;
  abn?: string | null;
  acn?: string | null;
  crNumber?: string | null;
  crStatus?: string | null;
  complianceStatus?: string | null;
  appointmentDate?: string | null;
  cessationDate?: string | null;
  conditions?: string | null;
  supervisionLevel?: string | null;
  piPolicyReference?: string | null;
  piExpiryDate?: string | null;
}

interface CreditRepModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCr?: CrData | null;
  onSuccess?: (crId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPOINTMENT_TYPES = [
  { value: "full", label: "Full" },
  { value: "limited", label: "Limited" },
  { value: "provisional", label: "Provisional" },
];

const ENTITY_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Company" },
];

const CR_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
];

const COMPLIANCE_STATUSES = [
  { value: "under_review", label: "Under Review" },
  { value: "compliant", label: "Compliant" },
  { value: "non_compliant", label: "Non-Compliant" },
  { value: "remediation_required", label: "Remediation Required" },
];

const SUPERVISION_LEVELS = [
  { value: "standard", label: "Standard" },
  { value: "enhanced", label: "Enhanced" },
  { value: "intensive", label: "Intensive" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreditRepModal({
  open,
  onOpenChange,
  editingCr,
  onSuccess,
}: CreditRepModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingCr?.id;

  // Form state
  const [entityName, setEntityName] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [entityType, setEntityType] = useState("individual");
  const [abn, setAbn] = useState("");
  const [acn, setAcn] = useState("");
  const [crNumber, setCrNumber] = useState("");
  const [crStatus, setCrStatus] = useState("pending");
  const [complianceStatus, setComplianceStatus] = useState("under_review");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [cessationDate, setCessationDate] = useState("");
  const [conditions, setConditions] = useState("");
  const [supervisionLevel, setSupervisionLevel] = useState("none");
  const [piPolicyReference, setPiPolicyReference] = useState("");
  const [piExpiryDate, setPiExpiryDate] = useState("");

  // Reset / pre-populate form when modal opens or editingCr changes
  useEffect(() => {
    if (open && editingCr) {
      setEntityName(editingCr.entityName ?? "");
      setAppointmentType(editingCr.appointmentType ?? "");
      setEntityType(editingCr.entityType ?? "individual");
      setAbn(editingCr.abn ?? "");
      setAcn(editingCr.acn ?? "");
      setCrNumber(editingCr.crNumber ?? "");
      setCrStatus(editingCr.crStatus ?? "pending");
      setComplianceStatus(editingCr.complianceStatus ?? "under_review");
      setAppointmentDate(editingCr.appointmentDate ?? "");
      setCessationDate(editingCr.cessationDate ?? "");
      setConditions(editingCr.conditions ?? "");
      setSupervisionLevel(editingCr.supervisionLevel ?? "none");
      setPiPolicyReference(editingCr.piPolicyReference ?? "");
      setPiExpiryDate(editingCr.piExpiryDate ?? "");
    } else if (open && !editingCr) {
      // Reset for create mode
      setEntityName("");
      setAppointmentType("");
      setEntityType("individual");
      setAbn("");
      setAcn("");
      setCrNumber("");
      setCrStatus("pending");
      setComplianceStatus("under_review");
      setAppointmentDate("");
      setCessationDate("");
      setConditions("");
      setSupervisionLevel("none");
      setPiPolicyReference("");
      setPiExpiryDate("");
    }
  }, [open, editingCr]);

  // Form validity
  const isValid = entityName.trim().length > 0 && appointmentType.length > 0;

  const isCompany = entityType === "company";

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/credit-reps", {
        entityName: entityName.trim(),
        appointmentType,
        entityType: entityType || undefined,
        abn: abn.trim() || undefined,
        acn: acn.trim() || undefined,
        crNumber: crNumber.trim() || undefined,
        crStatus,
        complianceStatus,
        appointmentDate: appointmentDate || undefined,
        cessationDate: cessationDate || undefined,
        conditions: conditions.trim() || undefined,
        supervisionLevel: supervisionLevel !== "none" ? supervisionLevel : undefined,
        piPolicyReference: piPolicyReference.trim() || undefined,
        piExpiryDate: piExpiryDate || undefined,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to create credit representative");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/credit-reps"] });
      toast({ title: "Credit representative created successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingCr?.id) throw new Error("No CR ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/credit-reps/${editingCr.id}`,
        {
          entityName: entityName.trim(),
          appointmentType,
          entityType: entityType || undefined,
          abn: abn.trim() || undefined,
          acn: acn.trim() || undefined,
          crNumber: crNumber.trim() || undefined,
          crStatus,
          complianceStatus,
          appointmentDate: appointmentDate || undefined,
          cessationDate: cessationDate || undefined,
          conditions: conditions.trim() || undefined,
          supervisionLevel: supervisionLevel !== "none" ? supervisionLevel : undefined,
          piPolicyReference: piPolicyReference.trim() || undefined,
          piExpiryDate: piExpiryDate || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to update credit representative");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/credit-reps"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${data.id}`],
      });
      toast({ title: "Credit representative updated successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-credit-rep-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Credit Representative" : "Add Credit Representative"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the credit representative details below."
              : "Add a new credit representative to the register."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Appointment Details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Appointment Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Appointment Type */}
              <div className="space-y-1.5">
                <Label>
                  Appointment Type <span className="text-destructive">*</span>
                </Label>
                <Select value={appointmentType} onValueChange={setAppointmentType}>
                  <SelectTrigger data-testid="select-appointment-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Entity Type */}
              <div className="space-y-1.5">
                <Label>Entity Type</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger data-testid="select-entity-type">
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CR Number */}
              <div className="space-y-1.5">
                <Label htmlFor="cr-number">CR Number</Label>
                <Input
                  id="cr-number"
                  data-testid="input-cr-number"
                  value={crNumber}
                  onChange={(e) => setCrNumber(e.target.value)}
                  placeholder="e.g. CR123456"
                />
              </div>

              {/* Appointment Date */}
              <div className="space-y-1.5">
                <Label htmlFor="appointment-date">Appointment Date</Label>
                <Input
                  id="appointment-date"
                  data-testid="input-appointment-date"
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Entity Details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Entity Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Entity Name */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="entity-name">
                  {isCompany ? "Company Name" : "Name"}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="entity-name"
                  data-testid="input-entity-name"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  placeholder={isCompany ? "Company or trading name" : "Full name"}
                />
              </div>

              {/* Company-only fields */}
              {isCompany && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="abn">ABN</Label>
                    <Input
                      id="abn"
                      data-testid="input-abn"
                      value={abn}
                      onChange={(e) => setAbn(e.target.value)}
                      placeholder="XX XXX XXX XXX"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="acn">ACN</Label>
                    <Input
                      id="acn"
                      data-testid="input-acn"
                      value={acn}
                      onChange={(e) => setAcn(e.target.value)}
                      placeholder="XXX XXX XXX"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Status
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* CR Status */}
              <div className="space-y-1.5">
                <Label>CR Status</Label>
                <Select value={crStatus} onValueChange={setCrStatus}>
                  <SelectTrigger data-testid="select-cr-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {CR_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Compliance Status */}
              <div className="space-y-1.5">
                <Label>Compliance Status</Label>
                <Select value={complianceStatus} onValueChange={setComplianceStatus}>
                  <SelectTrigger data-testid="select-compliance-status">
                    <SelectValue placeholder="Select compliance status" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLIANCE_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cessation Date */}
              <div className="space-y-1.5">
                <Label htmlFor="cessation-date">Cessation Date</Label>
                <Input
                  id="cessation-date"
                  data-testid="input-cessation-date"
                  type="date"
                  value={cessationDate}
                  onChange={(e) => setCessationDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Supervision & PI */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Supervision & Insurance
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Supervision Level */}
              <div className="space-y-1.5">
                <Label>Supervision Level</Label>
                <Select value={supervisionLevel} onValueChange={setSupervisionLevel}>
                  <SelectTrigger data-testid="select-supervision-level">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {SUPERVISION_LEVELS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* PI Policy Reference */}
              <div className="space-y-1.5">
                <Label htmlFor="pi-policy-ref">PI Policy Reference</Label>
                <Input
                  id="pi-policy-ref"
                  data-testid="input-pi-policy-ref"
                  value={piPolicyReference}
                  onChange={(e) => setPiPolicyReference(e.target.value)}
                  placeholder="Policy reference number"
                />
              </div>

              {/* PI Expiry Date */}
              <div className="space-y-1.5">
                <Label htmlFor="pi-expiry-date">PI Expiry Date</Label>
                <Input
                  id="pi-expiry-date"
                  data-testid="input-pi-expiry-date"
                  type="date"
                  value={piExpiryDate}
                  onChange={(e) => setPiExpiryDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="space-y-1.5">
            <Label htmlFor="conditions">Conditions</Label>
            <Textarea
              id="conditions"
              data-testid="input-conditions"
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder="Any conditions on the appointment..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Add Credit Representative"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
